import { CommonModule } from '@angular/common';
import {
  Component,
  HostBinding,
  input,
  Input,
  InputSignal,
  OnInit,
  signal,
  ViewChild,
  WritableSignal,
} from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { MatDividerModule } from '@angular/material/divider';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatStepper, MatStepperModule } from '@angular/material/stepper';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ActionEnum } from '@app/core/dto/abstract-action.dto';
import { MessageType } from '@app/core/dto/business-registration-form.dto';
import { GSTINVerificaitonDto } from '@app/core/dto/entity-verification.dto';
import {
  IbCreditFormDto,
  IbCreditFunctionName,
  IbCreditInputName,
} from '@app/core/dto/ib-credit-form.dto';
import { FormTypeEnum } from '@app/core/enums/form-type.enum';
import { InputTypeEnum } from '@app/core/enums/input-type.enum';
import { VerificationTypeEnum } from '@app/core/enums/verification-type.enum';
import { PipeModule } from '@app/core/pipe/pipe.module';
import { TimerService } from '@app/core/services/timer/timer.service';
import { StringUtils } from '@app/core/utils/string.utils';
import { BaseWidget } from '@app/widgets/base-widget/base-widget';
import { Observable } from 'rxjs';

@Component({
  selector: 'ib-credit-form',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatStepperModule,
    PipeModule,
    MatTooltipModule,
    MatDividerModule,
  ],
  templateUrl: './ib-credit-form.component.html',
  styleUrls: ['./ib-credit-form.component.scss'],
})
export class IBCreditFormComponent extends BaseWidget implements OnInit {
  @HostBinding('class') @Input() ngClass: any;
  @HostBinding('style') @Input() ngStyle: any;

  @Input() actionEvent: (event: any) => void;
  slot: InputSignal<any> = input();

  @ViewChild('ib_credit_stepper') stepper: MatStepper;

  data: any;
  ibCreditForm: FormGroup;
  ibCreditFormConfig: WritableSignal<any> = signal(null);
  inputTypeEnum = InputTypeEnum;
  isButtonDisabled: boolean = false;
  StringUtils = StringUtils;

  gstInVerified: WritableSignal<boolean> = signal(false);
  formDisabled: WritableSignal<boolean> = signal(true);
  verifyDisabled: WritableSignal<boolean> = signal(true);
  verificationInputValue: string;
  customErrors: WritableSignal<Record<string, any>> = signal({});
  MessageType = MessageType;
  isDisable: WritableSignal<Record<string, any>> = signal({});
  disableMessage: string = null;
  validationRules: Record<string, any>[] = [];
  placeHolder: WritableSignal<Record<string, string>> = signal({});
  isEmailExist: WritableSignal<boolean> = signal(null);
  inputName = IbCreditInputName;
  errorMessage: WritableSignal<Record<string, any>> = signal({});
  dataFromAction: WritableSignal<boolean> = signal(false);
  ibCreditFormFeilds: WritableSignal<Record<string, any>[]> = signal([]);
  verificationAction: Record<string, any>[] = [];
  showVerificationAction: WritableSignal<Record<string, any>> = signal({});
  IbCreditFunctionName = IbCreditFunctionName;
  isLinear: boolean = true;
  showLoader: WritableSignal<Record<string, boolean>> = signal({});
  selectedStepIndex: WritableSignal<number> = signal(0);
  disableSubmitButton: WritableSignal<boolean> = signal(false);
  handleInfoMessages: Record<string, any>[] = [];
  trackingAction: { [key: string]: any } = {};
  disableTill: number = 0;
  timerFor: string = null;
  timer$: Observable<number> | undefined;
  mobileNoVerified: WritableSignal<boolean> = signal(false);
  verifyGstInValue: WritableSignal<string> = signal(null);

  constructor(
    private fb: FormBuilder,
    private timerService: TimerService,
  ) {
    super();
  }

  ngOnInit(): void {
    this.data = this.slot().widget.data;
    this.data.payload = this.data?.formData?.payload;
    this.data.messages = this.data?.formData?.messages;
    this.ibCreditFormConfig.set(
      this.data?.renderableComponents[0]?.ibCreditForm?.stepper,
    );

    this.getFormFields();
    this.createForm();

    if (this.data?.payload) {
      this.dataFromAction.set(true);
      this.patchFormData(this.data?.payload);
      this.initializeTimers();
      this.showErrorOnLoad(this.data?.messages);
    }

    this.subscribeToFirstStepValidation();
  }

  /**
   * get all fields and store in
   * ibCreditFormFeilds
   */
  getFormFields() {
    this.ibCreditFormConfig().forEach((stepper) => {
      stepper?.fields.forEach((field) => {
        if (!StringUtils.isEmpty(field)) {
          this.ibCreditFormFeilds().push(field);
        }
      });
    });
  }

  createForm(): void {
    const formGroupConfig = {};
    this.ibCreditFormFeilds().forEach((field) => {
      const validators = [];

      if (field?.required) {
        validators.push(Validators.required);
      }
      if (field?.minlength) {
        validators.push(Validators.minLength(field?.minlength));
      }
      if (field?.pattern) {
        validators.push(Validators.pattern(field?.pattern));
      }
      if (field?.customValidationFunction) {
        const validationFn = this[field.customValidationFunction]?.();
        if (validationFn) {
          validators.push(validationFn);
        }
      }

      this.placeHolder.set({
        ...this.placeHolder(),
        [field?.name]: field?.placeholder,
      });

      formGroupConfig[field?.name] = [field?.defaultValue || '', validators];
    });

    this.ibCreditForm = this.fb.group(formGroupConfig);
  }

  /* 
      set data to the form when request received 
      from the backend.
    */
  patchFormData(payload: any): void {
    if (
      typeof payload === 'object' &&
      payload &&
      Object.keys(payload).length > 0
    ) {
      this.ibCreditForm.patchValue({
        [IbCreditInputName.MOBILE_NO]: payload?.[IbCreditInputName.MOBILE_NO],
        [IbCreditInputName.GST_IN]: payload?.[IbCreditInputName.GST_IN],
        [IbCreditInputName.FINANCE_PERSON_NAME]:
          payload?.[IbCreditInputName.FINANCE_PERSON_NAME],
        [IbCreditInputName.FINANCE_PHONE]:
          payload?.[IbCreditInputName.FINANCE_PHONE],
        [IbCreditInputName.OTP]: payload?.[IbCreditInputName.OTP],
      });

      this.gstInVerified.set(payload?.[IbCreditInputName.GST_IN_VERIFIED]);
      this.mobileNoVerified.set(
        payload?.[IbCreditInputName.MOBILE_NO_VERIFIED],
      );

      this.isFirstStepIsValid();
    }
  }

  initializeTimers(): void {
    this.ibCreditFormFeilds().forEach((field) => {
      // Check if the rightCTA object has a timer and initialize it
      const rightCTATimer: { duration: number; message: string } =
        field?.actions?.leftRightCTA?.rightCTA?.timer;

      if (rightCTATimer) {
        this.timerService.resetTimer(field.name, rightCTATimer.duration);
        this.subscribeToTimer(field);
      }
    });
  }

  subscribeToTimer(field: Record<string, any>): void {
    const fieldName: string = field.name;
    const timer$ = this.timerService.getTimerObservable(fieldName);
    // Subscribe to the timer observable and update the signal
    timer$?.subscribe((remaining) => {
      this.updateCTAButtonState(fieldName, remaining); // Call method to update the button state
    });
  }

  updateCTAButtonState(fieldName: string, remaining: number): void {
    this.isDisable.update((data) => ({
      ...data,
      [fieldName]: {
        rightCTA: remaining > 0,
      },
    }));
  }

  getTimerObservable(field: Record<string, any>) {
    const fieldName: string = field?.name;
    return this.timerService.getTimerObservable(fieldName);
  }

  getTimerMessage(fieldName: string): string {
    const field: Record<string, any> = this.ibCreditFormFeilds().find(
      (f) => f.name === fieldName,
    );
    return field?.actions?.leftRightCTA?.rightCTA?.timer?.message || '';
  }

  showErrorOnLoad(
    errorMessage: Record<string, { text: string; type: string }>,
  ) {
    if (errorMessage) {
      const updatedErrorMessages = { ...this.errorMessage };

      Object.keys(errorMessage).forEach((key) => {
        if (errorMessage[key].type === MessageType.ERROR) {
          const inputKey: string = Object.keys(IbCreditInputName).find(
            (k) =>
              IbCreditInputName[k as keyof typeof IbCreditInputName] === key,
          );

          this.setCustomError(IbCreditInputName[inputKey], true);
        }

        updatedErrorMessages[key] = {
          ...errorMessage[key],
        };
      });

      this.errorMessage.set(updatedErrorMessages);
    }
  }

  isFirstStepIsValid(): boolean {
    const validateInput: string[] = [
      IbCreditInputName.MOBILE_NO,
      IbCreditInputName.GST_IN,
      IbCreditInputName.FINANCE_PERSON_NAME,
      IbCreditInputName.FINANCE_PHONE,
    ];
    const isInvalid: boolean = validateInput.some((inputName) => {
      if (this.isInputInvalid(inputName)) {
        return true; // Return true if any input is invalid
      }
      return false; // Return false if the input is valid
    });

    this.formDisabled.update(() => isInvalid);
    this.isLinear =
      isInvalid || !this.gstInVerified() || !this.mobileNoVerified();

    if (isInvalid || !this.gstInVerified() || !this.mobileNoVerified()) {
      return false; // if first step is invalid
    } else {
      return true; // if first step is valid
    }
  }

  isInputInvalid(inputName: string): boolean {
    const control = this.ibCreditForm.get(inputName);
    if (!control) return false;
    return control.invalid;
  }

  /**
   * billingAddress is responsible for render the billing address form
   *
   * @param actionType
   */
  async onActionCall(actionType, index?: number) {
    if ([ActionEnum.SEND_OTP].includes(actionType)) {
      if (this.isInputInvalid(IbCreditInputName.MOBILE_NO)) {
        return;
      }
    }
    if (
      actionType === ActionEnum.VERIFY_OTP &&
      this.isInputInvalid(IbCreditInputName.OTP)
    ) {
      return;
    }

    if ([ActionEnum.VERIFY_GST].includes(actionType)) {
      if (this.isInputInvalid(IbCreditInputName.GST_IN)) {
        return;
      }
    }

    const ibCreditData: Partial<IbCreditFormDto> = this.getFormData();
    const additionalFormData = await this._getIbCreditFromData();

    const action = {
      actionData: {
        type: actionType,
        params: {
          ...ibCreditData,
          ...additionalFormData,
        },
      },
    };
    this.actionEvent(action);
  }

  getFormData() {
    return this.ibCreditForm.value;
  }

  /**
   * _getIbCreditFromData is responsible for get the
   * input field value
   * @returns BusinessRegistrationFormDto
   */
  protected async _getIbCreditFromData(): Promise<any> {
    this.ibCreditForm.markAllAsTouched();

    return {
      [IbCreditInputName.MOBILE_NO]: this.getInputFieldValue(
        IbCreditInputName.MOBILE_NO,
      ),
      [IbCreditInputName.GST_IN]: this.getInputFieldValue(
        IbCreditInputName.GST_IN,
      ),
      [IbCreditInputName.FINANCE_PERSON_NAME]: this.getInputFieldValue(
        IbCreditInputName.FINANCE_PERSON_NAME,
      ),
      [IbCreditInputName.FINANCE_PHONE]: this.getInputFieldValue(
        IbCreditInputName.FINANCE_PHONE,
      ),
      [IbCreditInputName.GST_IN_VERIFIED]: this.gstInVerified(),
      [IbCreditInputName.MOBILE_NO_VERIFIED]: this.mobileNoVerified(),
    };
  }

  getInputFieldValue(fieldName: string) {
    return (
      this.ibCreditForm.get(fieldName)?.value ?? this.data?.payload?.[fieldName]
    );
  }

  handleEvent(
    event: any,
    field: any,
    actionName: string,
    params: Record<string, any>,
  ): void {
    if (actionName && typeof this[actionName] === 'function') {
      this[actionName](event, field, params);
    }
  }

  handleInput(event: KeyboardEvent, field) {
    if (field?.isNumber) {
      const key = event.key;
      if (!/^[0-9]$/.test(key) && key !== 'Backspace' && key !== 'Enter') {
        event.preventDefault(); // Prevent the input if it's not valid
      }
    }
  }

  validateGstin() {
    const gstField = this.getIndividualField(IbCreditInputName.GST_IN);
    const gstValue = this.getInputFieldValue(IbCreditInputName.GST_IN);

    if (!this.isInputInvalid(IbCreditInputName.GST_IN)) {
      const payloadGstValue: string =
        this.data.payload?.[IbCreditInputName.GST_IN];
      const verifiedGstValue: string =
        this.verifyGstInValue() || payloadGstValue;

      if (verifiedGstValue && gstValue !== verifiedGstValue) {
        this.gstInVerified.set(false);
        gstField.isVerified = false;
        gstField.actions.verifyButton.showVerifyButton = true;
        this.updateFormField(gstField);
        this.formDisabled.set(true);
      } else {
        this.formDisabled.set(false);
      }

      if (!this.gstInVerified()) {
        gstField.isVerified = false;
        gstField.actions.verifyButton.showVerifyButton = true;
        this.updateFormField(gstField);
      } else {
        this.formDisabled.set(false);
      }
      return;
    }
    this.formDisabled.set(true);
  }

  validatePhone() {
    const phoneField = this.getIndividualField(IbCreditInputName.MOBILE_NO);
    const phoneValue = this.getInputFieldValue(IbCreditInputName.MOBILE_NO);
    if (!this.isInputInvalid(IbCreditInputName.MOBILE_NO)) {
      if (phoneValue !== this.data.payload?.[IbCreditInputName.MOBILE_NO]) {
        this.mobileNoVerified.set(false);
        phoneField.isVerified = false;
        phoneField.actions.verifyButton.showVerifyButton = true;
        this.updateFormField(phoneField);
        this.formDisabled.set(true);
      } else {
        this.formDisabled.set(false);
      }
      if (!this.mobileNoVerified()) {
        this.formDisabled.set(true);
        phoneField.isVerified = false;
        phoneField.actions.verifyButton.showVerifyButton = true;
        this.updateFormField(phoneField);
      }
      return;
    }
    this.formDisabled.set(true);
  }

  validateOtp() {
    if (!this.isInputInvalid(IbCreditInputName.MOBILE_NO)) {
      this.formDisabled.set(false);
      return;
    }

    this.formDisabled.set(true);
  }

  isSubmitButtonDisabled() {
    // Form should be disabled if:
    // 1. formDisabled is true (form has validation errors)
    // 2. mobile number is not verified
    // 3. GST is not verified
    return (
      this.formDisabled() || !this.mobileNoVerified() || !this.gstInVerified()
    );
  }

  checkAndTransformInput(event: Event, field): void {
    this.setCustomError(field?.name, false);
    this.deleteErrorMessage(field?.name);

    const inputElement = event.target as HTMLInputElement;
    const inputValue = inputElement.value;

    // Check conditions
    if (field?.isUpperCase) {
      inputElement.value = inputValue.toUpperCase();
      this.ibCreditForm.get(field?.name)?.setValue(inputElement.value);
    }
  }

  setCustomError(key: IbCreditInputName, customError: boolean) {
    const field = this.getIndividualField(key);
    const control = this.ibCreditForm.get(key);

    if (!control || !field) return;

    const validators: ValidatorFn[] = [];

    if (field.required) {
      validators.push(Validators.required);
    }

    if (field.minlength) {
      validators.push(Validators.minLength(field.minlength));
    }

    if (field.pattern) {
      validators.push(Validators.pattern(field.pattern));
    }

    if (field.customValidationFunction) {
      const fn = this[field.customValidationFunction]?.();
      if (fn) validators.push(fn);
    }

    // Always push your custom error validator — let Angular merge results
    validators.push(this.customErrorValidator(customError));

    control.setValidators(validators);

    // Ensure updated value is validated
    setTimeout(() => {
      control.updateValueAndValidity({ emitEvent: true });
      this.markInputTouchState(key, true); // optional
    }, 0);
  }

  deleteErrorMessage(fieldName: string) {
    delete this.errorMessage()?.[fieldName];
  }

  markInputTouchState(fieldName: string, isTouched: boolean) {
    const control = this.ibCreditForm.get(fieldName);
    control?.[isTouched ? 'markAsTouched' : 'markAsUntouched']();
  }

  customErrorValidator(customError: boolean): ValidatorFn {
    return (_control: AbstractControl): ValidationErrors | null => {
      return customError ? { customError: true } : null;
    };
  }

  getIndividualField(fieldName: IbCreditInputName) {
    return this.ibCreditFormFeilds().find((item) => item.name === fieldName);
  }

  isFieldEnabled(field: any): boolean {
    if (!field.condition) return true;
    return false;
  }

  updateStepIndex(index) {
    this.selectedStepIndex.set(index);
  }

  verifyGstNo() {
    this.fetchGstDetailsByGstNo(
      new GSTINVerificaitonDto(
        VerificationTypeEnum.GST,
        this.getInputFieldValue(IbCreditInputName.GST_IN),
      ),
    );
  }

  private _getButtonObjectByIndex(index: number) {
    if (index < 0 || index >= this.ibCreditFormConfig().length) {
      return null; // Handle out-of-bound index
    }
    const section = this.ibCreditFormConfig()[index];
    if (section && section.button) {
      return section.button;
    }
    return null; // Return null if no button is present for that index
  }

  private _updateActionButtonObject(
    index: number,
    newButtonConfig: Record<string, any>,
  ) {
    const currentConfig = this.ibCreditFormConfig();
    if (currentConfig[index] && currentConfig[index].button) {
      currentConfig[index].button = {
        ...this._getButtonObjectByIndex(index),
        ...newButtonConfig,
      };
      this.ibCreditFormConfig.set(currentConfig);
    }
  }

  private async fetchGstDetailsByGstNo(gstDto: GSTINVerificaitonDto, field?) {
    if (this.isInputInvalid(IbCreditInputName.GST_IN)) {
      return;
    }
    this.showLoader.set({
      ...this.showLoader(),
      [IbCreditInputName.GST_IN]: true,
    });
    return this.apiService
      .callVerificationApi({
        type: gstDto?.type,
        gstNumber: gstDto?.gstNumber,
        payload: {
          formType: FormTypeEnum.IB_CREDIT_FORM,
          formData: this.getFormData(),
        },
      })
      .subscribe({
        next: async (res) => {
          this.showLoader.set({
            ...this.showLoader(),
            [IbCreditInputName.GST_IN]: false,
          });
          const response = res.RESPONSE?.data;
          if (!response.status) {
            this.setCustomError(IbCreditInputName.GST_IN, true);
            this.errorMessage.set({
              ...this.errorMessage(),
              [IbCreditInputName.GST_IN]: {
                text: res.RESPONSE?.data?.message, // TODO: change this to response.message
                type: MessageType.ERROR,
              },
            });
          }

          if (response.status) {
            this.deleteErrorMessage(IbCreditInputName.GST_IN);
            this.setCustomError(IbCreditInputName.GST_IN, false);

            const gstField = this.getIndividualField(IbCreditInputName.GST_IN);
            gstField.isVerified = true;
            gstField.actions.verifyButton.showVerifyButton = false;
            this.verifyGstInValue.set(response.gst);
            this.updateFormField(gstField);

            this.gstInVerified.set(true);

            if (this.trackingAction?.GST && gstDto?.gstNumber) {
              const trackingActionReq = {
                tracking: this.trackingAction.GST,
              };
              const triggerTracking: boolean = super.getTrackingData(
                trackingActionReq,
                gstDto?.gstNumber,
              );
              if (triggerTracking) {
                this.actionEvent({
                  actionData: trackingActionReq,
                });
              }
            }
          }
        },
        error: (err) => {
          this.setCustomError(IbCreditInputName.GST_IN, true);
          this.errorMessage.set({
            ...this.errorMessage(),
            [IbCreditInputName.GST_IN]: {
              text: 'Gst details not founds',
              type: MessageType.ERROR,
            },
          });
        },
      });
  }

  updateFormField(updatedField) {
    const ibCreditFormFeilds: Record<string, any>[] = this.ibCreditFormFeilds();
    const fieldIndex = ibCreditFormFeilds.findIndex(
      (field) => field.name === updatedField.name,
    );

    if (fieldIndex !== -1) {
      // Create a new array with the updated field
      const updatedConfig = [
        ...ibCreditFormFeilds.slice(0, fieldIndex),
        updatedField,
        ...ibCreditFormFeilds.slice(fieldIndex + 1),
      ];

      // Set the updated array to the signal
      this.ibCreditFormFeilds.set(updatedConfig);
    }
  }

  subscribeToFirstStepValidation() {
    this.ibCreditForm
      .get(IbCreditInputName.GST_IN)
      ?.valueChanges.subscribe((value) => {
        this.disableSubmitButton.set(
          this.isInputInvalid(IbCreditInputName.GST_IN),
        );
        if (this.isInputInvalid(IbCreditInputName.GST_IN)) {
          this.validateGstin();
        }
      });

    this.ibCreditForm
      .get(IbCreditInputName.MOBILE_NO)
      ?.valueChanges.subscribe((value) => {
        if (value !== this.data?.payload?.[IbCreditInputName.MOBILE_NO]) {
          this.mobileNoVerified.set(false);
          const field = this.getIndividualField(IbCreditInputName.MOBILE_NO);
          field.actions.verifyButton.showVerifyButton = true;
          field.isVerified = false;
        }
      });

    this.ibCreditForm
      .get(IbCreditInputName.FINANCE_PERSON_NAME)
      ?.valueChanges.subscribe((value) => {
        this.disableSubmitButton.set(
          this.isInputInvalid(IbCreditInputName.FINANCE_PERSON_NAME),
        );
      });
    this.ibCreditForm
      .get(IbCreditInputName.FINANCE_PHONE)
      ?.valueChanges.subscribe((value) => {
        this.disableSubmitButton.set(
          this.isInputInvalid(IbCreditInputName.FINANCE_PHONE),
        );
      });

    this.ibCreditForm.events.subscribe((e) => {
      this.isFirstStepIsValid();
    });
  }

  private async _validateForm() {
    const fieldsWithErrors: string[] = [];

    Object.keys(this.ibCreditForm.controls).forEach((field) => {
      const control = this.ibCreditForm.get(field);
      if (control && control.invalid) {
        fieldsWithErrors.push(field);
        control.markAsTouched();
      }
    });

    if (this.ibCreditForm.invalid) {
      return true;
    }

    return false;
  }

  async validateCustomRules(): Promise<boolean> {
    let hasError: boolean = false;

    if (!this.mobileNoVerified()) {
      hasError = true;
      const control = this.ibCreditForm.get([IbCreditInputName.MOBILE_NO]);
      control?.markAsUntouched();
      this.errorMessage.set({
        [IbCreditInputName.MOBILE_NO]: {
          text: 'Please verify the Phone no.',
          type: MessageType.ERROR,
        },
      });
    }

    return hasError;
  }

  // submit ib credit form
  async submitIbCredit(event: Event, field, param?: Record<string, any>) {
    this.isFirstStepIsValid();

    if (!(await this._validateForm())) {
      const additionalFormData: Partial<IbCreditFormDto> =
        await this._getIbCreditFromData();

      const action = {
        actionData: {
          type: ActionEnum.SUBMIT_IB_CREDIT_FORM,
          params: {
            ...additionalFormData,
            ...param,
          },
        },
      };

      this.actionEvent(action);
    }
  }
}
